import apiClient from '@/api/client';

const TOKEN_KEY = 'mailproxy_token';
const REFRESH_KEY = 'mailproxy_refresh';

export interface LoginResult {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface CurrentUser {
  id: number;
  email: string;
  display_name: string;
  is_admin: boolean;
  is_active: boolean;
}

/** POST /auth/login, persist tokens in localStorage. Throws on bad credentials. */
export async function login(email: string, password: string): Promise<void> {
  const { data } = await apiClient.post<LoginResult>('/auth/login', { email, password });
  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_KEY, data.refresh_token);
}

/** Best-effort server logout, then clear local tokens. */
export async function logout(): Promise<void> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  try {
    if (refresh) await apiClient.post('/auth/logout', { refresh_token: refresh });
  } catch {
    // ignore — clearing local state below is what matters
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** Revoke ALL of the caller's sessions server-side, then clear local tokens. */
export async function logoutAll(): Promise<number> {
  let revoked = 0;
  try {
    const { data } = await apiClient.post<{ revoked: number }>('/auth/logout-all', {});
    revoked = data.revoked;
  } catch {
    // ignore — clearing local state below is what matters
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  return revoked;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

/** GET /auth/me — current authenticated user. */
export async function fetchMe(): Promise<CurrentUser> {
  const { data } = await apiClient.get<CurrentUser>('/auth/me');
  return data;
}
